import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { 
  CreditCard, Check, X, AlertCircle, Crown, Calendar, 
  DollarSign, RefreshCw, ExternalLink, Shield, Clock
} from 'lucide-react';
import { stripePromise, SUBSCRIPTION_PLANS, PaymentUtils } from '../utils/stripeConfig';
import { authService } from '../utils/supabase';
import { logger } from '../utils/logger';

const PaymentSettings = ({ currentUser }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const loadUserData = () => {
      if (currentUser) {
        const userSubscription = PaymentUtils.getUserSubscription(currentUser.id);
        setSubscription(userSubscription);
        
        // Set default plan based on user type
        const plan = PaymentUtils.getPlanForUserType(currentUser.user_type);
        setSelectedPlan(plan);
      }
      setLoading(false);
    };

    loadUserData();
  }, [currentUser]);

  const handleSubscribe = async (planId) => {
    if (!currentUser) return;
    
    setProcessing(true);
    try {
      const result = await PaymentUtils.createCheckoutSession(
        planId, 
        currentUser.id, 
        currentUser.user_type
      );
      
      if (result.success) {
        setSubscription(result.subscription);
        alert('Subscription activated successfully! You can now schedule meetings.');
      }
    } catch (error) {
      logger.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentUser || !subscription) return;

    if (!window.confirm('Are you sure you want to cancel your subscription? You will immediately lose access to meeting scheduling features.')) {
      return;
    }

    setProcessing(true);
    try {
      // Call the real cancel API endpoint
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      const result = await response.json();

      if (result.success) {
        // Update local state to reflect cancellation
        setSubscription({ ...subscription, status: 'canceled' });

        // Clear localStorage subscription to force user to see paywall
        localStorage.removeItem(`subscription_${currentUser.id}`);

        alert('Subscription canceled successfully. You no longer have access to meeting scheduling.');

        // Reload the page to immediately show the paywall
        window.location.reload();
      }
    } catch (error) {
      logger.error('Cancellation error:', error);
      alert(`Failed to cancel subscription: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Please log in to manage your subscription.</p>
      </div>
    );
  }

  // Healthcare providers get free access
  if (currentUser.user_type === 'customer') {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-green-800 mb-2">Free Access</h2>
              <p className="text-green-700 mb-4">
                As a healthcare provider, you have free access to all RepConnect features. 
                No payment required!
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SUBSCRIPTION_PLANS.healthcare_provider.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = PaymentUtils.hasValidSubscription(currentUser);
  const statusDisplay = PaymentUtils.getSubscriptionStatusDisplay(subscription);

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Subscription Status</h2>
        
        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  subscription.status === 'active' ? 'bg-green-500' : 
                  subscription.status === 'canceled' ? 'bg-red-500' : 'bg-yellow-500'
                }`}></div>
                <div>
                  <p className="font-medium text-gray-800">
                    {selectedPlan?.name} Plan
                  </p>
                  <p className={`text-sm ${statusDisplay.color}`}>
                    Status: {statusDisplay.text}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800">
                  {PaymentUtils.formatCurrency(selectedPlan?.price || 0)}
                  <span className="text-sm text-gray-500 font-normal">/month</span>
                </p>
              </div>
            </div>

            {subscription.current_period_end && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {subscription.status === 'active' ? 'Renews on' : 'Expires on'}: {' '}
                  {PaymentUtils.formatDate(subscription.current_period_end)}
                </span>
              </div>
            )}

            <div className="pt-4 border-t">
              {subscription.status === 'active' ? (
                <button
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {processing ? (
                    <span className="flex items-center space-x-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </span>
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              ) : (
                <button
                  onClick={() => handleSubscribe(selectedPlan?.priceId)}
                  disabled={processing}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {processing ? (
                    <span className="flex items-center space-x-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </span>
                  ) : (
                    'Reactivate Subscription'
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Active Subscription</h3>
            <p className="text-gray-600 mb-6">
              You need an active subscription to schedule meetings and access premium features.
            </p>
          </div>
        )}
      </div>

      {/* Access Warning for Non-Subscribers */}
      {!hasActiveSubscription && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <AlertCircle className="w-6 h-6 text-orange-600 mt-1" />
            <div>
              <h3 className="font-semibold text-orange-800 mb-2">Subscription Required</h3>
              <p className="text-orange-700 mb-4">
                You need an active subscription to schedule meetings with healthcare providers. 
                Subscribe now to unlock all features.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-orange-600">Restricted features without subscription:</p>
                <ul className="list-disc list-inside text-sm text-orange-600 space-y-1">
                  <li>Meeting scheduling and booking</li>
                  <li>Provider search and contact</li>
                  <li>Calendar integration</li>
                  <li>Expense tracking and reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plan Details */}
      {selectedPlan && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Crown className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">{selectedPlan.name} Plan</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold text-gray-800">
                    {PaymentUtils.formatCurrency(selectedPlan.price)}
                  </span>
                  <span className="text-gray-500">/month</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Billed monthly, cancel anytime</p>
              </div>

              {!hasActiveSubscription && (
                <button
                  onClick={() => handleSubscribe(selectedPlan.priceId)}
                  disabled={processing}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {processing ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>Subscribe Now</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div>
              <h4 className="font-medium text-gray-800 mb-3">Included Features:</h4>
              <div className="space-y-2">
                {selectedPlan.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security and Support Info */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Payment Security & Support</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">Secured by Stripe</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-700">Cancel anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-700">Automatic billing</span>
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Questions about billing? Contact our support team for assistance with your subscription.
            </p>
            <a
              href="mailto:support@support.meetwithreps.com?subject=RepConnect Support Request"
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Contact Support</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with Stripe Elements provider
const PaymentSettingsWrapper = ({ currentUser }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentSettings currentUser={currentUser} />
    </Elements>
  );
};

export default PaymentSettingsWrapper;